import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
} from "@mui/material";

import { db } from "../../../firebase-config";
import { v4 as uuidv4 } from "uuid";

import { GeoFirestore } from "geofirestore"; // Ensure correct import path
import firebase from "firebase/compat/app";
import LoadingButton from "../../Elements/LoadingButton/LoadingButton";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { buildSearchTerms } from "../../../utils/searchTerms";

dayjs.extend(utc);
dayjs.extend(timezone);

// import UpdateImageUrls from "../../Elements/UpdateImageUrls";
const geoFirestore = new GeoFirestore(db);

function buildEmbeddingText({ title, bands, venue_name, city, state, eventdescription, main_date }) {
  const bandNames = Array.isArray(bands)
    ? bands.map(b => b?.bandname).filter(Boolean).join(", ")
    : "";

  return [
    title || "",
    bandNames || "",
    eventdescription || "",
    venue_name || "",
    [city, state].filter(Boolean).join(", "),
    main_date || "", // keep raw string here; your CF/API can format/parse
  ].filter(Boolean).join(" • ");
}

const UploadEvents = ({ open, handleClose, venue }) => {
  const [jsonContent, setJsonContent] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Function to handle text change in text field
  const handleChange = (e) => {
    setJsonContent(e.target.value);
    setError("");
  };
  const removeCommas = (str) => {
    if (!str) return null;
    if (str.startsWith(",")) {
      str = str.slice(1);
    }
    if (str.endsWith(",")) {
      str = str.slice(0, -1);
    }
    return str;
  };

  // Function to validate and upload JSON content
  const handleUpload = () => {
    try {
      const parsedJson = JSON.parse(removeCommas(jsonContent));

      // Check if it's an array of objects
      if (
        Array.isArray(parsedJson) &&
        parsedJson.every((item) => typeof item === "object")
      ) {
        uploadEventsData(parsedJson)
          .then(() => {
            setMessage("All documents uploaded successfully");
            setIsLoading(false);
          })
          .finally(() => {
            setIsLoading(false);
          });
        // You can process the 'venue' prop here if necessary
        // handleClose();
      } else {
        setError("Invalid JSON: The content must be an array of objects.");
      }
    } catch (e) {
      setError(`Invalid JSON format. Error: ${e.message}`);
    }
  };
  const placeholderJson = `[
    {
      "fbid": "", 
      "main_date": "2025-01-29 16:30:00.0", 
      "ticket_url": "", 
      "title": "Chelsea Takami", 
      "eventdescription": "", 
      "user": "bulkupload",
      "eventtype": "Music"
    },
    {
      "fbid": "", 
      "main_date": "2025-01-30 16:30:00.0", 
      "ticket_url": "", 
      "title": "Chelsea Takami", 
      "eventdescription": "", 
      "user": "bulkupload",
      "eventtype": "Music"
    }
  ]`;
  const uploadEventsData = async (jsonData) => {
    setIsLoading(true);
    try {
      const geocollection = geoFirestore.collection("events");

      //   const bandsRef = query(collection(db, "bands"));
      //   const bandsSnapshot = await getDocs(bandsRef);
      //   const bandsMap = {};

      //   bandsSnapshot.forEach((doc) => {
      //     bandsMap[doc.id] = doc.data(); // Store the full band object
      //   });

      // Step 1: Check for existing events to avoid duplicates (like scrapeNYC.js)
      console.log(`[uploadEvents] Checking for duplicates in ${jsonData.length} events...`);
      const existingEventIds = new Set();
      
      // Check each event ID to see if it already exists
      for (const item of jsonData) {
        const eventId = item.id ? item.id + "" : uuidv4();
        try {
          const docRef = geocollection.doc(eventId);
          const snap = await docRef.get();
          if (snap.exists) {
            existingEventIds.add(eventId);
          }
        } catch (err) {
          console.warn(`[uploadEvents] Error checking event ${eventId}:`, err.message);
        }
      }

      // Filter out duplicates
      const newEvents = jsonData.filter((item) => {
        const eventId = item.id ? item.id + "" : null;
        return !eventId || !existingEventIds.has(eventId);
      });

      const skippedCount = jsonData.length - newEvents.length;
      console.log(`[uploadEvents] Found ${skippedCount} duplicates, uploading ${newEvents.length} new events`);
      
      if (skippedCount > 0) {
        setMessage(`Skipped ${skippedCount} duplicate event(s). Uploading ${newEvents.length} new event(s)...`);
      }

      // Step 2: Get all venues at once and create a dictionary for quick lookup
      //   const venuesSnapshot = await venuesRef.get();
      //   const venuesMap = {};
      //   venuesSnapshot.forEach((doc) => {
      //     venuesMap[doc.id] = doc.data();
      //   });

      const BATCH_SIZE = 400; // Maximum batch size
      let batch = geoFirestore.batch();
      let batchCounter = 0;

      for (const item of newEvents) {
        const eventId = item.id ? item.id + "" : uuidv4();
        const mainDate = item?.main_date;
        const modifiedDate = item?.modifieddate;

        let utcDate;
        try {
          utcDate = dayjs.tz(mainDate, "America/New_York").utc().toDate();
        } catch (e) {
          console.log("Error", e, item);
        }

        const timestamp = firebase.firestore.Timestamp.fromDate(utcDate);
        item.main_date = timestamp;

        item.modifieddate = firebase.firestore.Timestamp.fromDate(
          dayjs().utc().toDate()
        );
        item.title_lowercase = item?.title.toLowerCase();
        item.searchTerms = buildSearchTerms({
          title: item.title,
          bands: item.bands, // you set [] for bulk; that's fine
          venue_name: venue?.venue_name,
          venue_internallink: venue?.internallink || "",
          city: venue?.venue_city,
          state: venue?.venue_state,
        });
        item.city = venue?.venue_city;
        item.state = venue?.venue_state;
        item.venue_state = venue?.venue_state;
        item.venue_name = venue?.venue_name;
        item.address = venue?.address1;
        item.zip = venue?.VENUE_ZIP;
        item.coordinates = new firebase.firestore.GeoPoint(
          Number(venue?.lat) || 0,
          Number(venue?.lon) || 0
        );
        item.lat = venue?.lat;
        item.lon = venue?.lon;
        item.bands = [];
        item.venue = venue?.id + "";
        item.vid = venue?.id + "";
        item.venue_internallink = venue?.internallink || "";
        item.id = eventId;
        item.eventtype = item.eventtype || "Music";
        item.userId = "bulkupload";
        // --- Recommend/embedding metadata (match your single-add flow) ---
        item.embeddingText = buildEmbeddingText({
          title: item.title,
          bands: item.bands, // you currently set [] in bulk; OK
          venue_name: venue?.venue_name,
          city: venue?.venue_city,
          state: venue?.venue_state,
          eventdescription: item.eventdescription,
          main_date: mainDate, // original string
        });
        item.embeddingModel = "text-embedding-3-small";
        item.embeddingNeedsUpdate = true; // your Cloud Function will fill vectors
        // Optional breadcrumbs/versioning if you use them elsewhere:
        // item.embeddingVersion = 1;
        if (item?.date_utc === "") item.date_utc = null;
        if (item?.guests === "") item.guests = null;
        if (item?.highlight === "") item.highlight = null;
        if (item?.tzone === "") item.tzone = null;
        if (item?.title_url === "") item.title_url = null;

        // let bidArray = [];
        // for (let i = 1; i <= 5; i++) {
        //   const bidKey = `bid${i}`;
        //   if (
        //     item[bidKey] &&
        //     item[bidKey] !== null &&
        //     item[bidKey] !== 0 &&
        //     item[bidKey] !== ""
        //   ) {
        //     bidArray.push(item[bidKey] + "");
        //   }
        //   delete item[bidKey];
        // }

        // item.bandIds = bidArray;
        // const updatedBands = bidArray.map(
        //   (bandId) =>
        //     bandsMap[bandId] || { id: bandId, bandname: "Unknown Band" }
        // );
        // item.bands = updatedBands;

        const docRef = geocollection.doc(eventId);
        batch.set(docRef, item); // Add set operation to batch
        batchCounter++;

        // Commit the batch if it reaches the BATCH_SIZE
        if (batchCounter === BATCH_SIZE) {
          console.warn("Batch Comitted");
          await batch.commit();
          batch = geoFirestore.batch(); // Reset batch
          batchCounter = 0;
        }
      }

      // Commit any remaining documents in the final batch
      if (batchCounter > 0) {
        await batch.commit();
      }

      const finalMessage = skippedCount > 0 
        ? `Successfully uploaded ${newEvents.length} event(s). Skipped ${skippedCount} duplicate(s).`
        : `Successfully uploaded ${newEvents.length} event(s).`;
      
      setMessage(finalMessage);
      console.log("[uploadEvents]", finalMessage);
    } catch (error) {
      setError("Error Uploading JSON:", error);
      console.error("Error fetching JSON:", error);
    }
  };
  const clearState = () => {
    setIsLoading(false);
    setJsonContent("");
    setError("");
    setMessage("");
    handleClose();
  };
  return (
    <Dialog
      open={open}
      onClose={() => {
        clearState();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Upload Events for Venue: {venue?.venue_name}</DialogTitle>
      <DialogContent>
        <TextField
          //   label="JSON Array of Events"
          placeholder={placeholderJson}
          fullWidth
          multiline
          rows={10}
          variant="outlined"
          value={jsonContent}
          onChange={handleChange}
          error={!!error}
        />
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {message && !error && (
          <Typography color="success" variant="body2">
            {message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={clearState} color="secondary">
          Cancel
        </Button>
        <LoadingButton
          isLoading={isLoading}
          onClick={handleUpload}
          variant="contained"
          color="primary"
        >
          Upload
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadEvents;
